/**
 * ExamSessionSelectionService is a service class that handles the selection of exam sessions.
 * It interacts with TaskDataService, Restangular, and $q for data handling and asynchronous operations.
 */
class ExamSessionSelectionService {
    /**
     * Constructor for ExamSessionSelectionService.
     * @param {Object} TaskDataService - Service for task data operations.
     * @param {Object} Restangular - Service for RESTful operations.
     * @param {Object} $q - AngularJS promise service.
     */
    constructor(TaskDataService, Restangular, $q) {
        // 'ngInject'; // Remove this line if present
        this.TaskDataService = TaskDataService;
        this.Restangular = Restangular;
        this.$q = $q;
        this._loadSessionsPromise = null;
    }

    /**
     * Determines the default session based on the current time.
     * This method iterates through available session options and selects the one closest to the current date.
     */
    determineDefaultSession() {
        const now = moment().unix();
        let curDelta = 9999999999;
        let curDefaultSession = false;

        /**
         * Filters sessions to find the closest session to the current date.
         * @param {Object} s - Session object containing session details.
         */
        const filterDSession = (s) => {
            const ym = s.session.split("-");
            const month = ym[0];
            const year = ym[1];
            const sd = moment(`${year}-${month}-15`, 'YYYY-MM-DD');
            const su = sd.unix();
            const delta = now - su;
            const absDelta = Math.abs(delta);

            if (absDelta < curDelta) {
                curDefaultSession = s.session;
                curDelta = absDelta;
            }
        };

        angular.forEach(this.sessionOptions, filterDSession);
        if (curDefaultSession) {
            this.setSelectedSession(curDefaultSession);
        }
    }

    /**
     * Sets the selected session in sessionStorage.
     * @param {string} selSession - The session to be set as selected.
     */
    setSelectedSession(selSession) {
        sessionStorage.setItem("selectedExamSession", selSession);
    }

    /**
     * Retrieves the selected session from sessionStorage.
     * If no session is selected and allowDefault is true, it determines the default session.
     * @param {boolean} allowDefault - Flag to allow default session determination if no session is selected.
     * @returns {string|null} - The selected session or null if not found.
     */
    getSelectedSession(allowDefault = false) {
        const _preSel = sessionStorage.getItem("selectedExamSession");
        if (_preSel !== null && _preSel !== "null") {
            return _preSel;
        }
        if (allowDefault) {
            this.determineDefaultSession();
        }

        return sessionStorage.getItem("selectedExamSession");
    }

    /**
     * Retrieves the session options from the server and formats them for display.
     * @returns {Promise} - A promise that resolves with the formatted session options.
     */
    getSessionOptions() {
        if (this._loadSessionsPromise) {
            return this._loadSessionsPromise.promise;
        }

        this._loadSessionsPromise = this.$q.defer();
        const filteredSessions = [];

        /**
         * Filters and formats each session for display.
         * @param {Object} s - Session object containing session details.
         */
        const filterSession = (s) => {
            const ym = s.session.split("-");
            const month = ym[0];
            const month_pad = month.length === 1 ? '0' : '';
            const month_padded = month_pad + month;
            const month_name = moment.monthsShort(month - 1);
            const year = ym[1];

            filteredSessions.push({
                sessionOrder: `${year}-${month_padded}`,
                sessionDisplay: `${month_name} ${year}`,
                session: s.session
            });
        };

        /**
         * Callback function to process the received session list.
         * @param {Array} sessions - List of sessions received from the server.
         */
        const receiveSessionList = (sessions) => {
            angular.forEach(sessions, filterSession);
            this.sessionOptions = filteredSessions;
            this._loadSessionsPromise.resolve(this.sessionOptions);
        };

        this.Restangular.all('session').getList().then(receiveSessionList);

        return this._loadSessionsPromise.promise;
    }
}

// Add explicit dependency injection
ExamSessionSelectionService.$inject = ['TaskDataService', 'Restangular', '$q'];

export default ExamSessionSelectionService;